import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import {
  LoadingStage,
  LoadingStatus,
  LoadingStatusModal,
  StageInfo
} from "../components/Shared/Modals/LoadingStatusModal/LoadingStatusModal";
import { forceReload } from "../utils/nativeUtils/customActions";

interface LoadingStatusContextType {
  updateStage: (
    stage: LoadingStage,
    status: LoadingStatus,
    error?: { message: string; technicalDetails?: string }
  ) => void;
  resetLoading: () => void;
  isLoading: boolean;
}

const LoadingStatusContext = createContext<LoadingStatusContextType>({
  updateStage: () => {},
  resetLoading: () => {},
  isLoading: false
});

const SUCCESS_MODAL_DISPLAY_DURATION = 1000;

export const LoadingStatusProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [stages, setStages] = useState<StageInfo[]>([
    { stage: "authentication", status: "pending" },
    { stage: "organisation", status: "pending" },
    { stage: "venues", status: "pending" },
    { stage: "venueConfig", status: "pending" }
  ]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const updateStage = useCallback(
    (
      stage: LoadingStage,
      status: LoadingStatus,
      error?: { message: string; technicalDetails?: string }
    ) => {
      setStages((prev) => {
        const updated = prev.map((s) =>
          s.stage === stage ? { ...s, status, error } : s
        );

        if (status === "error") {
          setIsModalOpen(true);
        }

        if (updated.every((s) => s.status === "success")) {
          setTimeout(
            () => setIsModalOpen(false),
            SUCCESS_MODAL_DISPLAY_DURATION
          );
        }

        return updated;
      });
    },
    []
  );

  const resetLoading = useCallback(() => {
    setStages([
      { stage: "authentication", status: "pending" },
      { stage: "organisation", status: "pending" },
      { stage: "venues", status: "pending" },
      { stage: "venueConfig", status: "pending" }
    ]);
    setIsModalOpen(false);
  }, []);

  const handleRetry = useCallback(() => {
    resetLoading();
    forceReload();
  }, [resetLoading]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const isLoading = useMemo(
    () => stages.some((s) => s.status === "loading"),
    [stages]
  );

  const contextValue = useMemo(
    () => ({ updateStage, resetLoading, isLoading }),
    [updateStage, resetLoading, isLoading]
  );

  return (
    <LoadingStatusContext.Provider value={contextValue}>
      {children}
      <LoadingStatusModal
        isOpen={isModalOpen}
        stages={stages}
        onRetry={handleRetry}
        onClose={handleClose}
      />
    </LoadingStatusContext.Provider>
  );
};

export const useLoadingStatus = () => {
  const context = useContext(LoadingStatusContext);
  if (!context) {
    throw new Error(
      "useLoadingStatus must be used within LoadingStatusProvider"
    );
  }
  return context;
};
